import { toRaw } from "vue"
import type { Address } from "@types"

/**
 * Partie « titulaire / signataire » pour savoir si l’utilisateur connecté peut
 * prolonger en signature directe ({@link SourceExtendDialog} `isConsentHolder`).
 */
export type ConsentPartyForExtend = {
    companyId: string | null
    userId: string | null
    username: string | null
}

function normalizeEmail(value: string | null | undefined): string {
    return (value ?? "").trim().toLowerCase()
}

/**
 * Titulaire Homeys (carte compteur / consentement sur la source).
 */
export function titulaireToExtendParty(
    titulaire:
        | {
              user?: {
                  _id_user?: string | null
                  username?: string | null
              } | null
              company?: { _id_company?: string } | null
          }
        | null
        | undefined
): ConsentPartyForExtend | null {
    if (!titulaire?.user) return null
    return {
        companyId: titulaire.company?._id_company ?? null,
        userId: titulaire.user._id_user ?? null,
        username: titulaire.user.username ?? null,
    }
}

/**
 * Signataire (liste consentements legacy / {@link ConsentTable}).
 */
export function signataireToExtendParty(sign: {
    personne: "Physique" | "Morale"
    user: { _id_user?: string | null; username?: string | null }
    company?: { _id_company?: string } | null
}): ConsentPartyForExtend | null {
    if (!sign.user) return null
    if (sign.personne === "Morale" && sign.company?._id_company) {
        return {
            companyId: sign.company._id_company,
            userId: sign.user._id_user ?? null,
            username: sign.user.username ?? null,
        }
    }
    return {
        companyId: null,
        userId: sign.user._id_user ?? null,
        username: sign.user.username ?? null,
    }
}

/**
 * `true` si l’utilisateur courant est aligné sur le titulaire pour un prolongement
 * en parcours signature ({@link SourceConsentSign}), et non invitation.
 *
 * - **Personne morale** : même `_id_company` que sur le consentement.
 * - **Personne physique** (pas de société titulaire) : même `_id_user` ou même e-mail (`username`).
 */
export function isCurrentUserHolderForExtend(
    party: ConsentPartyForExtend | null,
    auth: { userId: string; username: string; companyId: string }
): boolean {
    if (!party) return false

    if (party.companyId) {
        return auth.companyId === party.companyId
    }

    if (party.userId && auth.userId && party.userId === auth.userId) {
        return true
    }

    const a = normalizeEmail(party.username)
    const b = normalizeEmail(auth.username)
    return Boolean(a && b && a === b)
}

/**
 * Copie plain de l’adresse source (évite de passer un proxy réactif au dialog / JSON).
 */
export function plainAddressForExtend(addr: unknown): Address | null {
    if (addr == null) return null
    const r = toRaw(addr) as Partial<Address>
    if (typeof r !== "object") return null
    return {
        street: r.street ?? "",
        zipcode: r.zipcode ?? "",
        city: r.city ?? "",
        country: r.country ?? "FR",
        citycode: r.citycode,
    }
}
